import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatusObject, createRequestStatusObject, createLazyList, buildReducers } from '../utils';
import {
	RecentConversation,
	Conversation,
	Feedback,
	SharedDocumentsResponse,
	AgentType
} from '../../types/ai-assistant';
import { LazyList, LazyListResponse } from '../../types/types';
import { API } from 'aws-amplify';
import { RootState } from '../store';
import { AppDispatch } from '../store';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';

export interface AIAssistantState {
	recentConversations: {
		chatAgent: LazyList<RecentConversation>;
		documentRequestAgent: LazyList<RecentConversation>;
	};
	recentArchivedConversations: LazyList<RecentConversation>;
	conversation: LazyList<Conversation>;
	singleConversation: LazyList<Conversation>;
	feedback: LazyList<Feedback>;
	singleFeedback: Feedback | null;
	searchUsersDocumentRequests: LazyList<Conversation>;
	personalSharedDocumentsRequests: LazyList<Conversation>;
	// sharedDocumentsRequests: SharedDocumentsResponse;

	createConversationRequest: RequestStatusObject;
	recentConversationRequest: RequestStatusObject;
	recentArchivedConversationRequest: RequestStatusObject;
	readSingleConversationRequest: RequestStatusObject;
	archiveConversationRequest: RequestStatusObject;
	deleteConversationRequest: RequestStatusObject;
	feedbackRequest: RequestStatusObject;
	listFeedbackRequest: RequestStatusObject;
	listFeedbackByIdRequest: RequestStatusObject;
	dismissFeedbackRequest: RequestStatusObject;
	searchUsersDocumentRequestsRequest: RequestStatusObject;
	storeFunctionCallResultsRequest: RequestStatusObject;
	personalSharedDocumentsRequestsRequest: RequestStatusObject;
	// sharedDocumentsRequestsRequest: RequestStatusObject;
}

const initialState: AIAssistantState = {
	recentConversations: {
		chatAgent: createLazyList(),
		documentRequestAgent: createLazyList()
	},
	recentArchivedConversations: createLazyList(),
	conversation: createLazyList(),
	singleConversation: createLazyList(),
	feedback: createLazyList(),
	singleFeedback: null,
	searchUsersDocumentRequests: createLazyList(),
	personalSharedDocumentsRequests: createLazyList(),
	// sharedDocumentsRequests: {
	// 	type: SharedDocumentsRequestType.ALL_SHARED_DOCUMENTS_REQUEST,
	// 	sharedDocumentsRequestsList: []
	// },
	createConversationRequest: createRequestStatusObject(),
	recentConversationRequest: createRequestStatusObject(),
	recentArchivedConversationRequest: createRequestStatusObject(),
	readSingleConversationRequest: createRequestStatusObject(),
	archiveConversationRequest: createRequestStatusObject(),
	deleteConversationRequest: createRequestStatusObject(),
	feedbackRequest: createRequestStatusObject(),
	listFeedbackRequest: createRequestStatusObject(),
	listFeedbackByIdRequest: createRequestStatusObject(),
	dismissFeedbackRequest: createRequestStatusObject(),
	searchUsersDocumentRequestsRequest: createRequestStatusObject(),
	storeFunctionCallResultsRequest: createRequestStatusObject(),
	personalSharedDocumentsRequestsRequest: createRequestStatusObject()
	// sharedDocumentsRequestsRequest: createRequestStatusObject()
};

export const createConversation = createAsyncThunk<
	LazyListResponse<Conversation> | SharedDocumentsResponse,
	{ userId: string; query: string; conversationId?: string; agentType?: AgentType; isOrganisation?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/createConversation', async ({ userId, query, conversationId, agentType, isOrganisation }) => {
	const response = await API.post('inspirusapi', `/kb/${userId}/query`, {
		headers: {},
		response: true,
		body: { query },
		queryStringParameters: { conversationId, agentType, isOrganisation }
	});
	return response.data;
});

// export const documentsRequestAgent = createAsyncThunk<
// 	SharedDocumentsResponse,
// 	{ userId: string; query: string; agentType: AgentType },
// 	{ dispatch: AppDispatch; state: RootState }
// >('aiAssistant/documentsRequestAgent', async ({ userId, query, agentType }) => {
// 	// TODO: add the real endpoint
// 	const response = await API.post('inspirusapi', `/kb/${userId}/query`, {
// 		headers: {},
// 		response: true,
// 		body: { query, agentType }
// 	});
// 	return response.data;
// });

export const searchUsersDocumentRequests = createAsyncThunk<
	LazyListResponse<Conversation>,
	{
		organisationId: string;
		next?: string | null;
		limit?: number;
		fullName?: string;
		startDate?: string;
		endDate?: string;
		overdue?: string;
		status?: string;
		requestStatus?: string;
		docType?: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'aiAssistant/searchUsersDocumentRequests',
	async ({
		organisationId,
		next,
		limit,
		fullName,
		startDate,
		endDate,
		overdue,
		status,
		requestStatus,
		docType = 'all'
	}) => {
		const response = await API.get('inspirusapi', `/organisations/${organisationId}/users/search`, {
			headers: {},
			response: true,
			queryStringParameters: {
				next,
				limit,
				fullName,
				startDate,
				endDate,
				overdue,
				status,
				requestStatus,
				docType
			}
		});
		return response.data;
	}
);

export const storeFunctionCallResults = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ messageId: string; functionCallResults: string },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/storeFunctionCallResults', async ({ messageId, functionCallResults }) => {
	const response = await API.post('inspirusapi', `/kb/messages/${messageId}/function-call-results`, {
		headers: {},
		response: true,
		body: { functionCallResults }
	});
	return response.data;
});

export const readConversation = createAsyncThunk<
	{ data: LazyListResponse<Conversation>; agentType: AgentType },
	{ userId: string; limit?: number; next?: string | null; agentType: AgentType },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/readConversation', async ({ userId, limit, next, agentType }) => {
	const response = await API.get('inspirusapi', `/kb/${userId}/conversations`, {
		headers: {},
		response: true,
		queryStringParameters: { archived: false, limit, next, agentType }
	});
	return {
		data: response.data,
		agentType
	};
});

export const readArchivedConversation = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ userId: string; limit?: number; next?: string | null; agentType: AgentType },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/readArchivedConversation', async ({ userId, limit, next, agentType }) => {
	const response = await API.get('inspirusapi', `/kb/${userId}/conversations`, {
		headers: {},
		response: true,
		queryStringParameters: { archived: true, limit, next, agentType }
	});
	return response.data;
});

export const readSingleConversation = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ userId: string; conversationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/readSingleConversation', async ({ userId, conversationId }) => {
	const response = await API.get('inspirusapi', `/kb/${userId}/conversations/${conversationId}`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return response.data;
});

export const archiveConversation = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ userId: string; conversationId: string; archive: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/archiveConversation', async ({ userId, conversationId, archive }) => {
	const response = await API.put('inspirusapi', `/kb/${userId}/conversations/${conversationId}/archive`, {
		headers: {},
		response: true,
		body: { archive }
	});
	return response.data;
});

export const deleteConversation = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ userId: string; conversationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/deleteConversation', async ({ userId, conversationId }) => {
	const response = await API.del('inspirusapi', `/kb/${userId}/conversations/${conversationId}`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return response.data;
});

export const sendFeedback = createAsyncThunk<
	LazyListResponse<Conversation>,
	{ userId: string; messageId: string; type: 'positive' | 'negative'; comment?: string },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/sendFeedback', async ({ userId, messageId, type, comment }) => {
	const response = await API.put('inspirusapi', `/kb/${userId}/messages/feedback`, {
		headers: {},
		response: true,
		queryStringParameters: { messageId },
		body: { feedback: type, comment }
	});
	return response.data;
});

export const listFeedback = createAsyncThunk<
	LazyListResponse<Feedback>,
	{ limit?: number; next?: string | null; dismissed?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/listFeedback', async ({ limit, next, dismissed }) => {
	const response = await API.get('inspirusapi', '/admins/feedback', {
		headers: {},
		response: true,
		queryStringParameters: { limit, next, dismissed }
	});
	return response.data;
});

export const listFeedbackById = createAsyncThunk<Feedback, { id: string }, { dispatch: AppDispatch; state: RootState }>(
	'aiAssistant/listFeedbackById',
	async ({ id }) => {
		const response = await API.get('inspirusapi', `/admins/feedback/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return response.data;
	}
);

export const dismissFeedback = createAsyncThunk<
	LazyListResponse<Feedback>,
	{ id: string; note?: string },
	{ dispatch: AppDispatch; state: RootState }
>('aiAssistant/dismissFeedback', async ({ id, note }) => {
	const response = await API.put('inspirusapi', `/admins/feedback/${id}`, {
		headers: {},
		response: true,
		body: note ? { note } : undefined,
		queryStringParameters: {}
	});
	return response.data;
});

export const listPersonalSharedDocumentsRequests = createAsyncThunk<
	LazyListResponse<Conversation>,
	{
		userId: string;
		next?: string | null;
		limit?: number;
		startDate?: string;
		endDate?: string;
		overdue?: string;
		status?: string;
		requestStatus?: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'aiAssistant/listPersonalSharedDocumentsRequests',
	async ({ userId, next, limit, startDate, endDate, overdue, status, requestStatus }) => {
		const response = await API.get('inspirusapi', `/users/${userId}/shared-documents-requests`, {
			headers: {},
			response: true,
			queryStringParameters: { next, limit, startDate, endDate, overdue, status, requestStatus }
		});
		return response.data;
	}
);

const aiAssistantSlice = createSlice({
	name: 'aiAssistant',
	initialState,
	reducers: {
		resetAIAssistant: (state) => {
			state = initialState;
			return state;
		},
		updateRecentConversation: (state, action) => {
			const { payload, agentType } = action.payload;
			state.recentConversations = {
				...state.recentConversations,
				[agentType]: payload
			};
		},
		updateRecentArchivedConversation: (state, action) => {
			state.recentArchivedConversations = action.payload;
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, createConversation, 'createConversationRequest', {
			onFulfilled: (state, payload) => {
				state.conversation = payload;
			}
		});
		buildReducers(builder, readConversation, 'recentConversationRequest', {
			onFulfilled: (state, payload) => {
				state.recentConversations = {
					...state.recentConversations,
					[payload.agentType]: payload.data
				};
			}
		});
		buildReducers(builder, readArchivedConversation, 'recentArchivedConversationRequest', {
			onFulfilled: (state, payload) => {
				state.recentArchivedConversations = payload;
			}
		});
		buildReducers(builder, readSingleConversation, 'readSingleConversationRequest', {
			onFulfilled: (state, payload) => {
				state.singleConversation = payload;
			}
		});
		buildReducers(builder, archiveConversation, 'archiveConversationRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.aiAssistant.archiveConversation.successMessage'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.aiAssistant.archiveConversation.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, deleteConversation, 'deleteConversationRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.aiAssistant.deleteConversation.successMessage'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.aiAssistant.deleteConversation.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, sendFeedback, 'feedbackRequest', {});
		buildReducers(builder, listFeedback, 'listFeedbackRequest', {
			onFulfilled: (state, payload) => {
				state.feedback = payload;
			}
		});
		buildReducers(builder, listFeedbackById, 'listFeedbackByIdRequest', {
			onFulfilled: (state, payload) => {
				state.singleFeedback = payload;
			}
		});
		buildReducers(builder, dismissFeedback, 'dismissFeedbackRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('Feedback dismissed successfully'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('Failed to dismiss feedback'), { variant: 'error' });
			}
		});
		buildReducers(builder, searchUsersDocumentRequests, 'searchUsersDocumentRequestsRequest', {
			onFulfilled: (state, payload) => {
				state.searchUsersDocumentRequests = payload;
			}
		});
		buildReducers(builder, storeFunctionCallResults, 'storeFunctionCallResultsRequest', {
			onFulfilled: (state, payload) => {
				state.searchUsersDocumentRequests = payload;
			}
		});
		buildReducers(builder, listPersonalSharedDocumentsRequests, 'personalSharedDocumentsRequestsRequest', {
			onFulfilled: (state, payload) => {
				state.personalSharedDocumentsRequests = payload;
			}
		});
		// buildReducers(builder, documentsRequestAgent, 'sharedDocumentsRequestsRequest', {
		// 	onFulfilled: (state, payload) => {
		// 		state.sharedDocumentsRequests = payload;
		// 	}
		// });
	}
});

export const { resetAIAssistant, updateRecentConversation, updateRecentArchivedConversation } =
	aiAssistantSlice.actions;
export default aiAssistantSlice.reducer;
